import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { CSpinner } from '@coreui/react-pro';
import Cookies from 'js-cookie';

import { callApi } from '../apiCaller';
import { isAuth, signOut } from '../helpers/auth';
import useTrailingUrl from '../helpers/hooks/useTrailingUrl';
import {
     addTimezoneLocal,
     getURLParams,
     mappingCurrentOrganizationUser,
     mappingCurrentUser,
     removeCommas,
     removeStorageApplication,
     removeURLParameter,
     toastError,
     tryParseJSON,
} from '../utils';
import {
     API_AUTH_CURRENT_USER,
     AUTH_PATH,
     COOKIE_NAMES,
     COOKIE_PARAMS,
     ENV_OBJ,
     FETCH_ADDRESS_URL,
     GUEST_PATH,
     LOCAL_DIRECT_PATH,
     LOCAL_ORG_USER,
     MODAL_LOGIN,
     REGEX_LAST_SLASH_CHARACTER,
     SYSTEM_ADMIN_ROUTES,
     USER_ROLES,
} from '../constants';

import { subscriberRoutes, guestRoutes, organizationRoutes } from '../routes';
import SystemAdminLayout from './system-admin/TheLayout';
import Subscriber from './subscriber/TheLayout';
import Guest from './guest/TheLayout';

import { setIsCalledCurrentUser, setPageLoading, setShow } from '../actions/general';
import { setSubscriberUser } from '../components/CMS/Subscriber/GlobalStates/CurrentUser/actions';
import { setCurrentCountry } from '../components/CMS/Subscriber/GlobalStates/CurrentCountry/actions';
import { setCurrentLocation } from '../components/CMS/Subscriber/GlobalStates/CurrentLocation/actions';
import { setSubscriberOrdersUser } from '../components/CMS/Guest/Checkout/action';

const TheLayout = () => {
     const dispatch = useDispatch();
     const location = useLocation();
     const navigate = useNavigate();

     useTrailingUrl();

     const user = useSelector((state) => state.subscriber.user);
     const pageLoading = useSelector((state) => state.general.pageLoading);
     const currentLocation = useSelector((state) => state.subscriber.currentLocation);

     const isAuthenticated = isAuth();

     const orgUserLocal = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const params = getURLParams();
     const pathName = location.pathname;

     const subscriberRoutesArrPath = subscriberRoutes.filter(({ path }) => path !== '*').map(({ path }) => removeCommas(path, '/'));
     const guestRoutesArrPath = guestRoutes.filter(({ path }) => path !== '*').map(({ path }) => removeCommas(path, '/'));
     const orgRoutesArrPath = organizationRoutes.filter(({ path }) => path !== '*').map(({ path }) => removeCommas(path, '/'));

     const fullPath = `${location.pathname}${location.search}${location.hash}`;
     const linkCannotNotAccess = [GUEST_PATH.PAYMENT_SUCCESS].map((path) => removeCommas(path, '/'));

     const url = new URL(window.location.href);
     const domain = ENV_OBJ[process.env.REACT_APP_ENV] ?? '';

     //#region cookie fast-testing
     const fastTestParam = url.searchParams.get(COOKIE_PARAMS.FAST_TESTING)?.replace(REGEX_LAST_SLASH_CHARACTER, '') === 'enabled';

     const enabledFastTesting = user?.userTestMode === true || fastTestParam;
     if (enabledFastTesting) {
          Cookies.set(COOKIE_NAMES.FAST_TEST, 'enabled', { domain });
     }

     if (user?.userTestMode === false) {
          Cookies.set(COOKIE_NAMES.FAST_TEST, 'disabled', { domain });
     }

     if (!user && url.searchParams.get(COOKIE_PARAMS.FAST_TESTING) === 'disabled') {
          Cookies.remove(COOKIE_NAMES.FAST_TEST, { domain });
     }
     //#endregion cookie fast-testing

     //#region cookie feature-flag
     const featureFlagParam = url.searchParams.get(COOKIE_PARAMS.FEATURE_FLAG)?.replace(/\//, '')?.trim();

     if (featureFlagParam && featureFlagParam !== '') {
          Cookies.set(COOKIE_NAMES.FEATURE_FLAG, featureFlagParam, { domain });
     }

     if (featureFlagParam === '') {
          Cookies.remove(COOKIE_NAMES.FEATURE_FLAG, { domain });
     }
     //#endregion cookie feature-flag

     //#region cookie current user
     const currentUserCookie = tryParseJSON(Cookies.get(COOKIE_NAMES.CURRENT_USER));
     const currentUser = mappingCurrentUser(user);

     if (currentUser && currentUser.id !== currentUserCookie.id) {
          setTimeout(() => {
               Cookies.set(COOKIE_NAMES.CURRENT_USER, JSON.stringify(currentUser), { domain });
          }, 500);
     }
     //#endregion cookie current user

     //#region cookie organization user
     const organizationUserCookie = tryParseJSON(Cookies.get(COOKIE_NAMES.ORGANIZATION_USER));
     const currentOrganizationUser = mappingCurrentOrganizationUser(orgUserLocal);

     if (currentOrganizationUser && currentOrganizationUser.organizationId !== organizationUserCookie.organizationId) {
          Cookies.set(COOKIE_NAMES.ORGANIZATION_USER, JSON.stringify(currentOrganizationUser), { domain });
     }

     if (organizationUserCookie && !currentOrganizationUser) {
          Cookies.remove(COOKIE_NAMES.ORGANIZATION_USER, { domain });
     }
     //#endregion cookie organization user

     const fetchUserInfo = () => {
          if (!isAuthenticated) return;

          dispatch(setPageLoading(true));
          dispatch(setIsCalledCurrentUser(false));

          callApi(API_AUTH_CURRENT_USER, 'GET').then((response) => {
               dispatch(setPageLoading(false));

               if (response.status !== 200) {
                    toastError(response);
                    signOut();
                    return;
               }

               const userResponse = response.data.user;
               const timezoneLocal = orgUserLocal?.organization?.timezone || userResponse.timezone;
               const countryLocal = orgUserLocal?.organization?.country || userResponse.country;

               addTimezoneLocal(timezoneLocal);
               dispatch(setSubscriberUser(userResponse));
               dispatch(setCurrentCountry(countryLocal));
               dispatch(setIsCalledCurrentUser(true));
          });
     };

     const fetchLocation = () => {
          if (currentLocation) return;

          fetch(FETCH_ADDRESS_URL)
               .then((response) => response.json())
               .then((data) => {
                    const { timezone, country_code, region } = data;
                    const newCurrentLocation = {
                         timezone: timezone?.id,
                         country_code,
                         region,
                    };
                    dispatch(setCurrentLocation(newCurrentLocation));
               })
               .catch((err) => console.error('Location error:: ', err));
     };

     // Handle check show login popup
     useEffect(() => {
          const loginUserTimer = setTimeout(() => {
               const checkLoginUser = location.search === '?login=true';
               if (checkLoginUser) {
                    signOut(false);
                    dispatch(setSubscriberOrdersUser(null));
                    dispatch(setShow(MODAL_LOGIN));
               }
          }, 100);

          return () => {
               clearTimeout(loginUserTimer);
          };
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     // Handle fetching data
     useEffect(() => {
          fetchUserInfo();
          fetchLocation();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     // Handle current user change and path name change
     useEffect(() => {
          const emailParam = params.email?.replace(/\//, '');
          let directFullPath = fullPath;

          if (!isAuthenticated && !user && emailParam) {
               navigate(AUTH_PATH.LOGIN, { replace: true });
               return;
          }

          if (isAuthenticated && user && emailParam) {
               if (user.email === emailParam) {
                    directFullPath = removeURLParameter(fullPath, 'email');
                    navigate(directFullPath, { replace: true });
               } else {
                    signOut();
               }
          }

          localStorage.setItem(LOCAL_DIRECT_PATH, directFullPath);
     }, [user, pathName]); // eslint-disable-line react-hooks/exhaustive-deps

     // Handle clean local storage and cookies related to users
     useEffect(() => {
          if (isAuthenticated) return;

          removeStorageApplication();
     }, [user, isAuthenticated]);

     const LayoutComponent = () => {
          const userRole = user?.role;
          const isAdminRoute = fullPath.includes(removeCommas(SYSTEM_ADMIN_ROUTES, '/'));
          const isAdminRole = [USER_ROLES.SYSTEM_ADMIN, USER_ROLES.SYSTEM_ADMIN_LOWER].includes(userRole);

          if (isAdminRole && isAdminRoute) {
               return <SystemAdminLayout />;
          }

          if (isAdminRole && !isAdminRoute) {
               return <Guest />;
          }

          if (userRole === USER_ROLES.SUBSCRIBER) {
               return <Subscriber />;
          }

          if (isAuth()) {
               return null;
          }

          const newPathName = removeCommas(pathName, '/');
          const pathNameArray = pathName && pathName !== '/' ? newPathName.split('/') : '';
          const firstPathName = pathNameArray[0];

          const guestRole =
               (pathName === '/' || guestRoutesArrPath.some((path) => path.includes(firstPathName))) && !linkCannotNotAccess.includes(newPathName);

          if (guestRole) {
               return <Guest />;
          }

          const showErrorCannotAccess =
               pathNameArray.length > 3 &&
               (subscriberRoutesArrPath.some((path) => path.includes(firstPathName)) ||
                    orgRoutesArrPath.some((path) => path.includes(firstPathName)));

          if (showErrorCannotAccess) {
               setTimeout(() => navigate(AUTH_PATH.LOGIN, { replace: true, state: { showErrorCannotAccess: true } }), 100);
          } else {
               setTimeout(() => navigate(AUTH_PATH.LOGIN, { replace: true }), 100);
          }

          return null;
     };

     if (pageLoading) {
          return (
               <div className="page-loading-container">
                    <CSpinner color="primary" />
               </div>
          );
     }

     return LayoutComponent();
};

export default TheLayout;
